import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Params } from '@angular/router';

/**
 * Story #3732 (R27): `/item/<id>` and `/<alias>/item/<id>` are the item addresses search engines
 * index and the sitemap lists. Unlike today's `#offer/<id>` they reach the server, so the edge can
 * serve the item's own SEO tags.
 *
 * The storefront opens an item from `#offer/<id>` on the store's own address, so these routes hand
 * over to exactly that address with a plain page redirect. An in-app redirect is not enough: the
 * store-loading guards that run next navigate again and drop the fragment, so the item would not
 * open. An id that is not a number lands on the store menu.
 */
export const itemLinkGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const document = inject(DOCUMENT);
  document.location.replace(
    itemLinkTarget(
      route.paramMap.get('storeAlias'),
      route.paramMap.get('offerId'),
      route.queryParams,
    ),
  );
  return false;
};

/** The store address that opens the item: `/#offer/<id>` or `/<alias>#offer/<id>`. */
export function itemLinkTarget(
  storeAlias: string | null,
  offerId: string | null,
  queryParams: Params = {},
): string {
  const store = storeAlias ? `/${encodeURIComponent(storeAlias)}` : '/';
  const query = new URLSearchParams(queryParams).toString();
  const withQuery = query ? `${store}?${query}` : store;
  return /^\d{1,18}$/.test(offerId ?? '')
    ? `${withQuery}#offer/${offerId}`
    : withQuery;
}
